import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { PropertyApplicationCreationDto, PropertyDto, UserApplications } from '@workspace/data-types';
import { ApplicationStore, UserStore } from '@workspace/ng-data-access';
import { EtxeEnumsNiceLabelUtils } from '../../../../utils/etxe-enums-nice-label.utils';
import {
  PropertyApplicationDialogComponent,
  PropertyApplicationDialogData,
} from '../dialog/property-application-dialog.component';

@Component({
  selector: 'etxe-property-candidacy-card',
  imports: [
    DecimalPipe,
    RouterLink,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
  ],
  templateUrl: './property-candidacy-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyCandidacyCardComponent {
  private readonly userStore = inject(UserStore);
  private readonly appStore = inject(ApplicationStore);
  private readonly dialog = inject(MatDialog);

  readonly property = input.required<PropertyDto>();
  readonly maxCandidates = input<number>(5);
  readonly isSubmitting = input<boolean>(false);
  readonly hideActions = input<boolean>(false);
  readonly isOwner = input<boolean | undefined>(undefined);
  readonly demoMode = input<boolean>(false);

  readonly apply = output<PropertyApplicationCreationDto>();

  readonly isSubscribed = computed<boolean>(() => {
    return this.demoMode() || this.userStore.isSubscribed();
  });

  readonly hasBeenSubscribed = computed<boolean>(() => {
    return this.userStore.hasBeenSubscribed();
  });

  readonly isApplied = computed<boolean>(() => {
    return !!this.property().application?.isApplied;
  });

  readonly candidatesCount = computed<number>(() => {
    return this.property().application?.acceptedCount ?? 0;
  });

  readonly appliedCount = computed<number>(() => {
    return this.property().application?.appliedCount ?? 0;
  });

  readonly isAccepting = computed<boolean>(() => {
    return this.property().application?.accepting ?? true;
  });

  readonly isOwnerUser = computed<boolean>(() => {
    if (this.demoMode()) return false;
    const inputVal = this.isOwner();
    if (inputVal !== undefined) return inputVal;

    // If we are in the OWNER application, this is always the owner's property
    if (this.appStore.name() === UserApplications.OWNER) {
      return true;
    }

    const prop = this.property();
    const userId = this.userStore.current()?._id;
    if (!userId) return false;

    const ownerObj = prop?.owner;
    const ownerId =
      typeof ownerObj === 'object' && ownerObj
        ? (ownerObj as unknown as Record<string, unknown>)['_id']
        : ownerObj;
    if (ownerId && ownerId.toString() === userId.toString()) return true;

    const createdByObj = prop?.createdBy;
    const createdById =
      typeof createdByObj === 'object' && createdByObj
        ? (createdByObj as unknown as Record<string, unknown>)['_id']
        : createdByObj;
    if (createdById && createdById.toString() === userId.toString()) return true;

    return false;
  });

  readonly isOwnerApp = computed<boolean>(() => {
    return this.appStore.name() === UserApplications.OWNER;
  });

  readonly shouldHideActions = computed<boolean>(() => {
    return this.hideActions() || (this.isOwnerUser() && this.isOwnerApp());
  });

  readonly mandateTypeLabel = computed<string>(() => {
    return EtxeEnumsNiceLabelUtils.mandateType(this.property().mandate?.type);
  });

  readonly mandateDurationLabel = computed<string>(() => {
    return EtxeEnumsNiceLabelUtils.mandateDuration(this.property().mandate?.duration);
  });

  readonly sellingPriceEuros = computed<number | null>(() => {
    const cents = this.property().financials?.sellingPriceCents;
    return cents != null ? cents / 100 : null;
  });

  readonly targetCommissionRate = computed<number | null>(() => {
    return this.property().mandate?.targetCommissionRate ?? null;
  });

  readonly estimatedCommissionEuros = computed<number | null>(() => {
    const price = this.sellingPriceEuros();
    const rate = this.targetCommissionRate();
    if (price == null || rate == null) return null;
    return Math.round((price * rate) / 100);
  });

  readonly totalFaiEuros = computed<number | null>(() => {
    const price = this.sellingPriceEuros();
    if (price == null) return null;
    const comm = this.estimatedCommissionEuros();
    return comm != null ? price + comm : price;
  });

  readonly prioritiesLabels = computed<string[]>(() => {
    const priorities = this.property().mandate?.priorities;
    if (!priorities || !Array.isArray(priorities)) return [];
    return priorities.map((p) => EtxeEnumsNiceLabelUtils.sellerPriority(p)).filter(Boolean);
  });

  readonly initialApplicationDto = computed<PropertyApplicationCreationDto | undefined>(() => {
    const prop = this.property();
    if (!prop || !prop._id) return undefined;
    return {
      propertyId: prop._id,
      engagement: {
        commissionRate: prop.mandate?.targetCommissionRate ?? 3.5,
        services: [],
      },
      message: '',
    };
  });

  openForm(): void {
    const dialogRef = this.dialog.open<
      PropertyApplicationDialogComponent,
      PropertyApplicationDialogData,
      PropertyApplicationCreationDto
    >(PropertyApplicationDialogComponent, {
      width: '100%',
      maxWidth: '720px',
      data: {
        property: this.property(),
        initialDto: this.initialApplicationDto(),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.apply.emit(result);
      }
    });
  }
}
